import { Fragment, useCallback, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { userAccess } from "../../../../utils/CommonFunction";

import { feedbackIntegration } from "@sentry/react";
import { usePermissions } from "../../../../utils/permission";
import ClientIcon from "./icons/ClientIcon";
import DocumentIcon from "./icons/DocumentIcon";
import HireIcon from "./icons/HireIcon";
import LogoutIcon from "./icons/LogoutIcon";
import ProfileIcon from "./icons/ProfileIcon";
import SendFeedbackIcon from "./icons/SendFeedbackIcon";
import TasksIcon from "./icons/TasksIcon";
import TeamifiedIcon from "./icons/TeamifiedIcon";

import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { Grid, Tooltip, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { clearClientCode } from "../../../../redux/Client/clientSlice";
import { clearCompany } from "../../../../redux/Company/companySlice";
import {
  clearAllWorkableClients,
  clearHireCandidateStaging,
  clearHireList,
} from "../../../../redux/Hire/hireSlice";
import { logOut } from "../../../../redux/Login/loginSlice";
import { clearMemberList } from "../../../../redux/People/peopleSlice";
import { Status } from "../../../../services/ApiStatus";
import { setClientHeader } from "../../../../services/BaseApi";
import { userLogout } from "../../../../services/OnBoarding";
import LogoutSpinner from "../../LoadingSpinner/LogoutSpinner";
import NavlinkWithTooltip from "./NavlinkWithTooltip";
const feedback = feedbackIntegration({
  // Disable injecting the default widget
  autoInject: false,

  // Customise the title of the feedback widget
  showBranding: false,
  formTitle: "Send feedback",
  submitButtonLabel: "Send feedback",
  messagePlaceholder:
    "What is your feedback? Did you find a bug? What did you expect?",
  successMessageText:
    "Thank you for your feedback! The development team have been notified.",
});
const NavItems = ({ drawerOpen, close, openDrawer }) => {
  const { pathname } = useLocation();
  const { isTeamMember, empId, firstName, lastName } = userAccess();
  const {
    Hire,
    Invoice,
    Client,
    Task,
    Performance,
    Documents,
    TeamMember,
    TimeManagement,
  } = usePermissions();
  const [loading, setLoading] = useState(false);

  const routes = [
    {
      path: "/tasks",
      label: "My Tasks",
      IconComponent: TasksIcon,
      condition: () => Task,
    },
    {
      path: "/jobs",
      label: "Hire",
      IconComponent: HireIcon,
      condition: () => Hire,
      subRoutes: [
        {
          path: "/job-request",
          label: "Open Jobs ",
        },

        {
          path: "/interviews",
          label: "Interviews",
        },
      ].filter((el) => el),
      activeWhen: ["/jobs", "/job-request", "/interviews"],
    },

    {
      path: "/client",
      label: "Clients",
      IconComponent: ClientIcon,
      activeWhen: ["/team", "/leave", "/timesheets", "/performance"],
      subRoutes: [
        {
          label: "My teams",
          path: "/team",
          subRoutes: [
            {
              path: "/team-members",
              label: "Team Members",
              condition: () => TeamMember,
            },
            {
              path: "/leave",
              label: "Leave",
              condition: () => TimeManagement,
            },
            {
              path: "/timesheets",
              label: "Timesheets",
              condition: () => TimeManagement,
            },
            {
              path: isTeamMember ? `/performance/${empId}` : "/performance",
              label: "Performance Pulse",
              condition: () => Performance,
            },
          ],
        },
        {
          path: "/invoices",
          label: "Invoices",
          condition: () => Invoice,
        },
        // {
        //   path: "/shop",
        //   label: "Shop",
        //   condition: () => Shop,
        // },
      ],
    },

    // {
    //   path: "/opportunities",
    //   label: "Opportunities",
    //   IconComponent: OpportunitiesIcon,
    //   condition: () => Opportunity,
    // },

    // {
    //   path: "/time",
    //   label: "Time Management",
    //   IconComponent: TimeIcon,
    //   condition: () => TimeManagement,
    // },

    // {
    //   path: "/attendance",
    //   label: "Attendance",
    //   IconComponent: AttendanceIcon,
    //   condition: () => Attendance,
    // },

    {
      path: "/documents",
      label: "Documents",
      IconComponent: DocumentIcon,
      condition: () => Documents,
    },
    {
      path: "/teamified",
      label: "Admin",
      IconComponent: TeamifiedIcon,
      condition: () => Client,
      subRoutes: [
        {
          path: "/users",
          label: "Users",
        },
        {
          path: "/client",
          label: "Clients",
        },
      ],
    },
  ];
  const [openSubMenus, setOpenSubMenus] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accessToken, refreshToken } = useSelector((state) => state.login);

  const logOutUser = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      let dataObj = { Token: accessToken, refreshToken: refreshToken };
      const response = await userLogout(dataObj);
      if (response?.status === Status.Success) {
        dispatch(logOut());
        dispatch(clearCompany());
        dispatch(clearHireList());
        dispatch(clearAllWorkableClients());
        dispatch(clearHireCandidateStaging());
        dispatch(clearMemberList());
        dispatch(clearClientCode());
        setClientHeader(0);
        setLoading(false);
        navigate("/");
      } else {
        setLoading(false);
        alert.error(response.data.errorMessage);
      }
    } catch (error) {
      setLoading(false);
      alert.error(error);
    }
  };

  const handleFeedbackClick = async (e) => {
    e.preventDefault();

    const form = await feedback.createForm({
      useSentryUser: {
        name: "fullName",
        email: "email",
      },
    });
    form.appendToDom();
    form.open();
  };

  const handleNavItemClick = useCallback(() => {
    openDrawer?.(); // Open the drawer when any nav item is clicked
  }, [openDrawer]);

  const handleNavLinkClick = useCallback(
    (event, route) => {
      if (route.subRoutes) {
        event.preventDefault();
        setOpenSubMenus((prevOpenSubMenus) => ({
          ...prevOpenSubMenus,
          [route.path]: !!!prevOpenSubMenus[route.path],
        }));
      } else {
        close?.();
      }
    },
    [close],
  );

  const isRouteActive = useCallback(
    (route) => {
      if (route.subRoutes) {
        return null;
      }
      // Check if the current pathname is included in the route's activeWhen array
      if (route.activeWhen && route.activeWhen.includes(pathname)) {
        return true;
      }

      return false;
    },
    [pathname],
  );

  const createNavItems = useCallback(
    (route, level = 0) => {
      if (route.condition && !route.condition()) return null;
      const isActive = isRouteActive(route, pathname);

      const Icon = route.IconComponent;
      const isSubMenuOpen = !!openSubMenus[route.path];

      return (
        <Fragment key={route.path}>
          <Tooltip
            placement="right"
            title={
              !drawerOpen ? (
                <Typography variant="body1">{route.label}</Typography>
              ) : (
                ""
              )
            }
          >
            <div className="nav-item">
              <NavLink
                to={route.path}
                exact={"true"}
                className={isActive ? "active" : ""}
                style={{
                  paddingLeft: `${level === 1 ? "56px" : level === 2 ? "70px" : ""}`,
                }}
                onClick={(e) => {
                  handleNavLinkClick(e, route);
                  handleNavItemClick(route);
                }}
              >
                <Grid
                  container
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Grid item>
                    {Icon ? <Icon active={isActive} /> : null}
                    <span
                      className="nav-label"
                      style={{
                        display: "inline-block",
                        maxWidth: drawerOpen ? 200 : 0.1, // Use 0.1 instead of 0
                        opacity: drawerOpen ? 1 : 0,
                        marginLeft: `${level === 1 ? "12px" : level === 2 ? "18px" : ""}`,
                        transition:
                          "max-width 0.3s cubic-bezier(0.4,0,0.2,1), margin-left 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s 0.1s",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                      }}
                    >
                      {route.label}
                    </span>
                  </Grid>
                  {drawerOpen && route.subRoutes && (
                    <span className="sub-menu-toggle">
                      {isSubMenuOpen ? (
                        <KeyboardArrowDown />
                      ) : (
                        <KeyboardArrowRight />
                      )}
                    </span>
                  )}
                </Grid>
              </NavLink>
            </div>
          </Tooltip>
          {drawerOpen &&
            route.subRoutes &&
            isSubMenuOpen &&
            route.subRoutes.map((el) => createNavItems(el, level + 1))}
        </Fragment>
      );
    },
    [
      handleNavItemClick,
      handleNavLinkClick,
      isRouteActive,
      openSubMenus,
      pathname,
      drawerOpen,
    ],
  );

  return (
    <>
      {loading && <LogoutSpinner />}
      <nav
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          marginTop: "8px",
        }}
      >
        {routes.map((el) => createNavItems(el))}
        <NavlinkWithTooltip
          label="Send Feedback"
          drawerOpen={drawerOpen}
          icon={<SendFeedbackIcon />}
          onClick={handleFeedbackClick}
          style={{ marginTop: "auto" }}
          path="/feedback"
        />
        <NavlinkWithTooltip
          label={`${firstName} ${lastName}`}
          drawerOpen={drawerOpen}
          icon={<ProfileIcon />}
          path="/profile"
        />
        <NavlinkWithTooltip
          label="Logout"
          drawerOpen={drawerOpen}
          icon={<LogoutIcon />}
          onClick={logOutUser}
          path="/logout"
        />
      </nav>
    </>
  );
};

export default NavItems;
